// ============================================
// Daily Performance Chart
// Messages + Leads trend over time with stacked bars
// ============================================

import { ChartSkeleton } from '../../../components/layout/LoadingState';
import { formatNumber } from '../../../utils/formatters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Semantic colors from dashboard-design skill
const colors = {
  messages: '#3B82F6', // Blue - Messages
  leads: '#10B981',    // Green - Leads
};

interface DailyData {
  date: string;
  messages: number;
  leads: number;
}

interface DailyPerformanceChartProps {
  data?: DailyData[];
  isLoading: boolean;
}

export function DailyPerformanceChart({ data, isLoading }: DailyPerformanceChartProps) {
  if (isLoading) {
    return <ChartSkeleton height={350} />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-[350px] flex items-center justify-center">
        <p className="text-gray-500">Không có dữ liệu conversions</p>
      </div>
    );
  }

  // Format date for display
  const chartData = data.map((item) => ({
    ...item,
    dateLabel: new Date(item.date).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
    }),
  }));

  // Calculate totals
  const totalMessages = data.reduce((sum, d) => sum + d.messages, 0);
  const totalLeads = data.reduce((sum, d) => sum + d.leads, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Conversions Theo Ngày</h3>
          <p className="text-sm text-gray-500">Messages & Leads hàng ngày</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.messages }}></div>
            <span className="text-gray-600">{formatNumber(totalMessages)} Messages</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.leads }}></div>
            <span className="text-gray-600">{formatNumber(totalLeads)} Leads</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatNumber}
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              formatNumber(value),
              name === 'messages' ? 'Messages' : 'Leads',
            ]}
            labelFormatter={(label) => `Ngày ${label}`}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Legend
            formatter={(value) => (value === 'messages' ? 'Messages' : 'Leads')}
            wrapperStyle={{ paddingTop: '10px' }}
          />
          <Bar
            dataKey="messages"
            fill={colors.messages}
            radius={[4, 4, 0, 0]}
            name="messages"
          />
          <Bar
            dataKey="leads"
            fill={colors.leads}
            radius={[4, 4, 0, 0]}
            name="leads"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
